import { action, computed, makeObservable, observable } from 'mobx'

import { Block } from './Block'
import { ContentModule } from './Module'

export type ModuleCollectionConfig = {
  trainingId?: string
  modules?: ContentModule[]
}

export class ModuleCollection {
  trainingId: string

  private _modules = observable(new Map<string, ContentModule>())

  private _selectedItemUuid = observable.box<string | undefined>(undefined)

  get modules(): ContentModule[] {
    return Array.from(this._modules.values())
  }

  get selectedItemUuid(): string | undefined {
    return this._selectedItemUuid.get()
  }

  get selectedItem(): ContentModule | Block | undefined {
    const selectedUuid = this._selectedItemUuid.get()
    if (!selectedUuid) return undefined

    const module = this._modules.get(selectedUuid)
    if (module) {
      return module
    }

    const block = this.getBlockByUuid(selectedUuid)
    if (block) {
      return block
    }

    return undefined
  }

  get flatItems(): Array<ContentModule | Block> {
    const items: Array<ContentModule | Block> = []

    for (const module of this.modules) {
      items.push(module)
      items.push(...module.blocks)
    }

    return items
  }

  constructor({ trainingId = '0', modules = [] }: ModuleCollectionConfig = {}) {
    this.trainingId = trainingId

    modules.forEach((contentModule) => {
      this._modules.set(contentModule.uuid, contentModule)
    })

    // Auto-select first item if available
    this.selectFirstItem()

    makeObservable(this, {
      trainingId: observable,
      modules: computed,
      selectedItem: computed,
      flatItems: computed,
      addItem: action,
      editItem: action,
      deleteItem: action,
      setSelected: action,
      reorder: action,
      next: action,
      back: action,
    })
  }

  empty(): boolean {
    return this._modules.size === 0
  }

  /**
   * Clears the internal modules collection and resets the selected item UUID to undefined.
   * Use when syncing state with AI agent
   *
   * @return {void} Does not return a value.
   */
  clear() {
    this._modules.clear()
    this._selectedItemUuid.set(undefined)
  }

  setSelected(uuid?: string): ModuleCollection {
    this._selectedItemUuid.set(uuid)
    return this
  }

  /**
   * Retrieves an item by its unique identifier (UUID).
   *
   * @param {string} uuid - The unique identifier of the item to retrieve.
   * @return {ContentModule | Block | undefined} The item associated with the given UUID, or undefined if not found.
   */
  getItem(uuid: string): ContentModule | Block | undefined {
    return this._modules.get(uuid) || this.getBlockByUuid(uuid)
  }

  addItem(item: ContentModule | Block): ModuleCollection {
    if (item instanceof ContentModule) {
      this.addModule(item)
    } else if (item instanceof Block) {
      this.addBlock(item)
    }
    return this
  }

  editItem(uuid: string, updatedItem: ContentModule | Block): ModuleCollection {
    if (updatedItem instanceof ContentModule) {
      this.editModule(uuid, updatedItem)
    } else if (updatedItem instanceof Block) {
      this.editBlock(uuid, updatedItem)
    }
    return this
  }

  deleteItem(uuid: string): ModuleCollection {
    const module = this._modules.get(uuid)
    if (module) {
      this.deleteModule(uuid)
    } else {
      this.deleteBlock(uuid)
    }
    return this
  }

  next(): ModuleCollection {
    let currentUuid = this._selectedItemUuid.get()

    // If no selection, select the first item
    if (!currentUuid) {
      const items = this.flatItems
      const firstItem = items[0]
      if (firstItem) {
        this._selectedItemUuid.set(firstItem.uuid)
        currentUuid = firstItem.uuid
      } else {
        // No items available, return early
        return this
      }
    }

    const items = this.flatItems
    const currentIndex = items.findIndex((item) => item.uuid === currentUuid)

    if (currentIndex === -1) {
      throw new Error(`Current item ${currentUuid} not found in collection`)
    }

    const nextIndex = currentIndex + 1

    if (nextIndex < items.length) {
      const nextItem = items[nextIndex]
      if (nextItem) {
        this._selectedItemUuid.set(nextItem.uuid)
      }
    }
    // If at last item, stay there (no change to selection)

    // TODO: Set completed property when reaching the last item
    // (Future: track course completion state for UI feedback)

    return this
  }

  isLastItem(): boolean {
    const currentUuid = this._selectedItemUuid.get()
    if (!currentUuid) return false
    const items = this.flatItems
    const currentIndex = items.findIndex((item) => item.uuid === currentUuid)
    return currentIndex === items.length - 1
  }

  back(): ModuleCollection {
    const currentUuid = this._selectedItemUuid.get()

    // Assertion: should never be called without selection
    if (!currentUuid) {
      throw new Error('back() called with no selected item')
    }

    const items = this.flatItems
    const currentIndex = items.findIndex((item) => item.uuid === currentUuid)

    if (currentIndex === -1) {
      throw new Error(`Current item ${currentUuid} not found in collection`)
    }

    const previousIndex = currentIndex - 1

    if (previousIndex >= 0) {
      const previousItem = items[previousIndex]
      if (previousItem) {
        this._selectedItemUuid.set(previousItem.uuid)
      }
    }
    // If at first item, stay there (no change to selection)

    return this
  }

  private addModule(contentModule: ContentModule): ModuleCollection {
    this._modules.set(contentModule.uuid, contentModule)
    this._selectedItemUuid.set(contentModule.uuid)
    return this
  }

  private editModule(uuid: string, updatedModule: ContentModule): ModuleCollection {
    this._modules.set(uuid, updatedModule)
    return this
  }

  private deleteModule(uuid: string): ModuleCollection {
    this.selectPreviousOrNextForModule(uuid)
    this._modules.delete(uuid)
    return this
  }

  private editBlock(blockUuid: string, updatedBlock: Block): ModuleCollection {
    const parentModule = this.findParentModule(blockUuid)
    if (parentModule) {
      parentModule.updateBlock(blockUuid, updatedBlock)
    }
    return this
  }

  private deleteBlock(blockUuid: string): ModuleCollection {
    const parentModule = this.findParentModule(blockUuid)
    if (parentModule) {
      this.selectPreviousOrNextForBlock(blockUuid, parentModule)
      parentModule.deleteBlock(blockUuid)
    }
    return this
  }

  private addBlock(block: Block): ModuleCollection {
    const targetModule = this.findTargetModuleForBlock()
    if (targetModule) {
      targetModule.addBlock(block)
      this._selectedItemUuid.set(block.uuid)
    }
    return this
  }

  private selectFirstItem(): void {
    const modules = this.modules
    if (modules.length > 0 && modules[0]) {
      this._selectedItemUuid.set(modules[0].uuid)
    }
  }

  private findTargetModuleForBlock(): ContentModule | undefined {
    const selectedUuid = this._selectedItemUuid.get()
    if (!selectedUuid) {
      return this.modules[0] // If nothing selected, add to first module
    }

    // If selected item is a module, add block to it
    const selectedModule = this._modules.get(selectedUuid)
    if (selectedModule) {
      return selectedModule
    }

    // If selected item is a block, add to its parent module
    return this.findParentModule(selectedUuid)
  }

  private getBlockByUuid(uuid: string): Block | undefined {
    for (const module of this._modules.values()) {
      const block = module.getBlockByUuid(uuid)
      if (block) {
        return block
      }
    }
    return undefined
  }

  private findParentModule(blockUuid: string): ContentModule | undefined {
    for (const module of this._modules.values()) {
      if (module.getBlockByUuid(blockUuid)) {
        return module
      }
    }
    return undefined
  }

  private selectPreviousOrNext<T extends { uuid: string }>(
    items: T[],
    deletedUuid: string,
    fallbackUuid?: string
  ): void {
    const deletedIndex = items.findIndex((item) => item.uuid === deletedUuid)

    if (deletedIndex > 0) {
      // Select previous item
      const previousItem = items[deletedIndex - 1]
      if (previousItem) {
        this._selectedItemUuid.set(previousItem.uuid)
      }
    } else if (items.length > 1 && items[1]) {
      // Select next item (first remaining item)
      this._selectedItemUuid.set(items[1].uuid)
    } else if (fallbackUuid) {
      // Fallback to provided UUID (e.g., parent module)
      this._selectedItemUuid.set(fallbackUuid)
    } else {
      // No items left
      this._selectedItemUuid.set(undefined)
    }
  }

  private selectPreviousOrNextForModule(deletedModuleUuid: string): void {
    this.selectPreviousOrNext(this.modules, deletedModuleUuid)
  }

  private selectPreviousOrNextForBlock(
    deletedBlockUuid: string,
    parentModule: ContentModule
  ): void {
    this.selectPreviousOrNext(parentModule.blocks, deletedBlockUuid, parentModule.uuid)
  }

  reorder(
    orderedStructure: Array<{ id: string; children?: Array<{ id: string }> }>
  ): ModuleCollection {
    const reorderedModules = new Map<string, ContentModule>()

    orderedStructure.forEach(({ id: moduleUuid, children }) => {
      const module = this._modules.get(moduleUuid)
      if (module) {
        reorderedModules.set(moduleUuid, module)

        if (children && children.length > 0) {
          const reorderedBlocks = children
            .map(({ id: blockUuid }) => module.getBlockByUuid(blockUuid))
            .filter((block): block is Block => block !== undefined)
          module.blocks = reorderedBlocks
        }
      }
    })

    this._modules.replace(reorderedModules)
    return this
  }
}
